import React, { forwardRef } from "react";
import { ReactSVG } from "react-svg";
import { Controller, useFormContext } from "react-hook-form";
import { setSVGColor } from "../SVGHelper";
import useOptionallyControlled from "../hooks/useOptionallyControlled";
import ReactSelect, { components } from "react-select";

import "../style/colors.scss";
import "../style/typography.scss";
import "../style/inputs.scss";
import "../style/buttons.scss";
import "../style/select.scss";

import upArrow from "../icon/dashboard/Arrow/IOS/Up.svg";
import downArrow from "../icon/dashboard/Arrow/IOS/Down.svg";
import BrOnNewlineChar from "./BrOnNewlineChar";

/**
 * Custom dropdown indicator component for making react-select dropdown
 * indicator support react-svg.
 */
const CustomDropdownIndicator = (props) => {
    const isOpen = props.selectProps.menuIsOpen;
    return (
        <components.DropdownIndicator {...props}>
            <ReactSVG src={isOpen ? upArrow : downArrow} beforeInjection={setSVGColor("var(--state-color-icon)")} />
        </components.DropdownIndicator>
    );
};

/**
 * Custom control component to include a label.
 */
const CustomContainer = ({ children, ...props }) => {
    const { label, noShrink } = props.selectProps;
    return (
        <components.SelectContainer {...props}>
            <label className="select__label">{label || (noShrink ? "\u00A0" : "")}</label>
            {children}
        </components.SelectContainer>
    );
};

/**
 * Select component for rendering a custom dropdown select input.
 * Uses the react-select library, and supports all of its props via restProps.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {string} props.label - The label text next to the input.
 * @param {boolean} props.noShrink - If true, the component will take up the space for the label even if it's not used.
 * @param {string} props.name - The name of the input for form-related purposes.
 * @param {string} props.value - The controlled value of the select input.
 * @param {Object} props.formOptions - The form options object.
 * @param {boolean} props.disabled - If true, the select input will be disabled.
 * @param {Function} props.filterFunc - The function used to filter the value.
 * @param {Function} props.onChange - The function called when the select value changes.
 * @param {Array} props.options - The array of options for the select input.
 * @param {string} [props.placeholder='Select'] - The placeholder text for the select input.
 * @param {React.Ref} ref - The ref object for the select input.
 * @param {...any} props.restProps - Any other props to pass to the underlying input.
 * @returns {React.Element} The rendered Select component.
 */
const Select = (
    {
        label, // label text next to input
        noShrink, // if true, component will take up the space for the label even if its not used
        name, // name of the input for forms stuff
        value: controlledValue,
        formOptions,
        disabled,
        filterFunc = (value) => value, // return filtered value
        onChange,
        options,
        placeholder = "Select",
        ...restProps // any other props to pass to the underlying ReactSelect component
    },
    ref,
) => {
    const formContext = useFormContext();

    const [propsForInput] = useOptionallyControlled(name, controlledValue, onChange, formOptions, filterFunc);

    const selectId = `select-${name}`;
    const error = formContext?.formState?.errors?.[name]?.message;

    const sharedProps = {
        ref,
        id: selectId,
        name,
        components: {
            SelectContainer: CustomContainer,
            DropdownIndicator: CustomDropdownIndicator,
        },
        menuPortalTarget: document.body, // makes the dropdown options visible when the parent element has "overflow: hidden" set
        menuShouldBlockScroll: true, // workaround for preventing the dropdown options from jumping around when scrolling if menuPortalTarget is used (same workaround used by MUI's select component)
        unstyled: true,
        options,
        placeholder,
        className: `select ${error ? "select--error" : ""}`,
        classNamePrefix: "select",
        isDisabled: disabled,
        noShrink, // for custom container
        label, // for custom container
        ...restProps,
    };

    return (
        <div className={`input-container ${error ? "error" : ""} ${disabled ? "disabled" : ""}`}>
            {formContext ? (
                <Controller // ReactSelect is a controlled component, so this is required to use it with react-hook-form https://react-hook-form.com/get-started#IntegratingControlledInputs
                    control={formContext?.control}
                    render={({ field }) => <ReactSelect {...field} {...sharedProps} />}
                    {...propsForInput}
                />
            ) : (
                <ReactSelect {...propsForInput} {...sharedProps} />
            )}

            <span
                className="input-info-text"
                style={{
                    visibility: error ? "visible" : "hidden", // hide if no text here, needed if noShrink puts blank space here
                }}
            >
                {error ? <BrOnNewlineChar text={error} /> : noShrink ? "\u00A0" : ""}
            </span>
        </div>
    );
};

export default forwardRef(Select);
